import { User, UserRole } from '../types';
import { api } from './api';

interface RegisterRequest {
  email: string;
  password: string;
  profile: {
    firstName: string;
    lastName: string;
  };
  role: UserRole;
}

interface AuthResponse {
  user: User;
  token: string;
}

interface RegistrationResponse {
  message: string;
  email: string;
}

// Mock users for development (matches database seed data)
const MOCK_USERS = {
  'coach@test.com': {
    id: '11111111-1111-1111-1111-111111111111',
    email: 'coach@test.com',
    role: 'COACH' as UserRole,
    profile: {
      firstName: 'John',
      lastName: 'Coach',
      clubId: '88888888-8888-8888-8888-888888888888'
    },
    createdAt: new Date(),
    updatedAt: new Date()
  },
  'admin@test.com': {
    id: '22222222-2222-2222-2222-222222222222',
    email: 'admin@test.com',
    role: 'CLUB_ADMIN' as UserRole,
    profile: {
      firstName: 'Jane',
      lastName: 'Admin',
      clubId: '88888888-8888-8888-8888-888888888888'
    },
    createdAt: new Date(),
    updatedAt: new Date()
  },
  'parent@test.com': {
    id: '33333333-3333-3333-3333-333333333333',
    email: 'parent@test.com',
    role: 'PARENT' as UserRole,
    profile: {
      firstName: 'Bob',
      lastName: 'Parent'
    },
    createdAt: new Date(),
    updatedAt: new Date()
  }
};

// Check if we're in development mode and backend is not available
const isDevelopmentMode = process.env.NODE_ENV === 'development';

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    // Try real API first, fall back to mock if connection fails
    try {
      const response = await api.post<{ data: AuthResponse }>('/auth/login', {
        email,
        password,
      });
      return response.data.data;
    } catch (error: any) {
      // If connection refused and in development, use mock
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock authentication for development');
        
        // Check mock credentials
        const mockUser = MOCK_USERS[email as keyof typeof MOCK_USERS];
        
        if (mockUser && password === 'password123') {
          const mockToken = `mock-jwt-token-${Date.now()}`;
          // Store mock user data for session persistence
          localStorage.setItem('mockUserData', JSON.stringify(mockUser));
          return {
            user: mockUser,
            token: mockToken
          };
        } else {
          throw new Error('Invalid credentials');
        }
      }
      throw error;
    }
  },

  async register(data: RegisterRequest): Promise<RegistrationResponse> {
    // Try real API first, fall back to mock if connection fails
    try {
      const response = await api.post<{ data: RegistrationResponse }>('/auth/register', data);
      return response.data.data;
    } catch (error: any) {
      // If connection refused and in development, use mock registration
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock registration for development');
        
        // Check if user already exists
        if (MOCK_USERS[data.email as keyof typeof MOCK_USERS]) {
          throw new Error('User already exists');
        }
        
        // Return mock registration response (no tokens)
        return {
          message: 'Registration successful! Please check your email to verify your account.',
          email: data.email
        };
      }
      throw error;
    }
  },

  async getProfile(): Promise<User> {
    try {
      const response = await api.get<{ data: User }>('/auth/profile');
      return response.data.data;
    } catch (error: any) {
      // If connection refused and in development, return mock user from token
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        const token = localStorage.getItem('token');
        if (token && token.startsWith('mock-jwt-token')) {
          // Return the currently logged in mock user
          const currentUserEmail = localStorage.getItem('currentUserEmail');
          if (currentUserEmail) {
            // Check if it's a pre-seeded mock user
            const mockUser = MOCK_USERS[currentUserEmail as keyof typeof MOCK_USERS];
            if (mockUser) {
              return mockUser;
            }
            
            // For newly registered users, create a mock user profile
            // Extract user info from localStorage or create a basic profile
            const mockUserData = localStorage.getItem('mockUserData');
            if (mockUserData) {
              return JSON.parse(mockUserData);
            }
            
            // Fallback: create a basic mock user profile
            return {
              id: `mock-user-${Date.now()}`,
              email: currentUserEmail,
              role: 'COACH' as UserRole,
              profile: {
                firstName: 'Mock',
                lastName: 'User',
                clubId: '88888888-8888-8888-8888-888888888888'
              },
              createdAt: new Date(),
              updatedAt: new Date()
            };
          }
        }
      }
      throw error;
    }
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.put<{ data: User }>('/auth/profile', data);
    return response.data.data;
  },

  async verifyRegistration(token: string): Promise<{ message: string }> {
    const response = await api.post<{ data: { message: string } }>('/profile/registration/verify', {
      token,
    });
    return response.data.data;
  },

  async resendRegistrationVerification(email: string): Promise<{ message: string }> {
    const response = await api.post<{ data: { message: string } }>('/profile/registration/resend', {
      email,
    });
    return response.data.data;
  },

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await api.post<{ data: { message: string } }>('/auth/forgot-password', {
      email,
    });
    return response.data.data;
  },

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ data: { message: string } }>('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data.data;
  },

  async getPermissions(): Promise<{ permissions: any; capabilities: any; role: string }> {
    try {
      const response = await api.get<{ data: { permissions: any; capabilities: any; role: string } }>('/auth/permissions');
      return response.data.data;
    } catch (error: any) {
      // If connection refused and in development, return mock permissions
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        const token = localStorage.getItem('token');
        if (token && token.startsWith('mock-jwt-token')) {
          const currentUserEmail = localStorage.getItem('currentUserEmail');
          const mockUser = currentUserEmail ? MOCK_USERS[currentUserEmail as keyof typeof MOCK_USERS] : null;
          const role = mockUser?.role || 'COACH';
          
          return {
            permissions: {
              canCreateTeams: role === 'COACH',
              canCreateMultipleTeams: false, // Mock users don't have unlimited teams
              canManagePlayers: role === 'COACH',
              canCreateAssessments: role === 'COACH',
              canViewAllClubData: role === 'CLUB_ADMIN',
              canViewOwnChildren: role === 'PARENT',
              canDeleteResources: role === 'COACH',
              canModifyData: role === 'COACH',
              readOnlyAccess: ['CLUB_ADMIN', 'PARENT'].includes(role)
            },
            capabilities: {
              maxTeams: 10
            },
            role
          };
        }
      }
      throw error;
    }
  },

  async registerWithPayment(data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    clubName: string;
    address: string;
    quantity?: number;
    tierName?: string;
  }): Promise<{ checkoutUrl: string; sessionId: string; email: string; message: string }> {
    const response = await api.post<{ data: { checkoutUrl: string; sessionId: string; email: string; message: string } }>(
      '/auth/register-with-payment',
      data
    );
    return response.data.data;
  },

  async registerCoachWithPayment(data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }): Promise<{ checkoutUrl: string; email: string; message: string }> {
    const response = await api.post<{ data: { checkoutUrl: string; email: string; message: string } }>(
      '/auth/register-coach-with-payment',
      data
    );
    return response.data.data;
  },

  async verifyEmail(token: string): Promise<{ user: User; token: string; message: string }> {
    const response = await api.get<{ data: { user: User; token: string; message: string } }>(
      `/auth/verify-email/${token}`
    );
    return response.data.data;
  },

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    const response = await api.post<{ data: { message: string } }>(
      '/auth/resend-verification',
      { email }
    );
    return response.data.data;
  },
};